import React, { useState, useRef, useEffect } from "react";
import styles from "./ImageExportMenu.module.css";
import {
  captureSvgAsPng,
  captureIframeAsPng,
  downloadBlob,
  downloadText,
} from "../CodeBlock/VisualizationExporter";

interface ExportMenuProps {
  iframeRef: React.RefObject<HTMLIFrameElement>;
  code: string;
  language: string;
  vizId?: string;
  svgContent?: string;
}

const FILE_EXTENSIONS: Record<string, string> = {
  mermaid: "mmd",
  "vega-lite": "json",
  html: "html",
  bpmn: "bpmn",
  csv: "csv",
  svg: "svg",
};

const SVG_LANGUAGES = new Set(["mermaid", "svg"]);

function deriveFileBaseName(
  code: string,
  language: string,
  fallback: string,
): string {
  let name: string | null = null;
  const trimmed = code.trim();

  if (language === "mermaid") {
    const match = trimmed.match(/^[a-zA-Z0-9_-]+\s*$/m);
    if (match) name = match[0];
  } else if (language === "html") {
    const titleMatch = trimmed.match(/<title>([^<]+)<\/title>/i);
    if (titleMatch) name = titleMatch[1];
  } else if (
    language === "vega-lite" ||
    language === "vegalite" ||
    language === "vega" ||
    language === "json"
  ) {
    try {
      const spec = JSON.parse(trimmed);
      if (spec && typeof spec.title === "string") {
        name = spec.title;
      } else if (spec && typeof spec.name === "string") {
        name = spec.name;
      }
    } catch {
      // ignore JSON parse errors
    }
  } else if (language === "csv") {
    // Simple, predictable name for tables
    name = "table";
  }

  if (!name) name = fallback;

  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 80) || fallback
  );
}

const ExportMenu: React.FC<ExportMenuProps> = ({
  iframeRef,
  code,
  language,
  vizId = "visualization",
  svgContent,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [exporting, setExporting] = useState<"png" | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const isSvgBased = SVG_LANGUAGES.has(language);
  const isHtml = language === "html";
  const isVegaLite =
    language === "vega-lite" || language === "vegalite" || language === "vega";
  const isMermaid = language === "mermaid";
  const isCsv = language === "csv";

  const showImageOption = isHtml || isVegaLite || isMermaid || isSvgBased;
  const baseName = deriveFileBaseName(code, language, vizId);

  // Close on click outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen]);

  /**
   * Get SVG string — either from prop or extracted from iframe.
   */
  const getSvgString = (): string | null => {
    if (svgContent) return svgContent;

    const doc = iframeRef.current?.contentDocument;
    if (!doc) return null;
    const svgEl = doc.querySelector("svg");
    if (!svgEl) return null;

    return new XMLSerializer().serializeToString(svgEl);
  };

  const handleExportPng = async () => {
    setExporting("png");
    try {
      let blob: Blob | null = null;

      if (isSvgBased) {
        const svg = getSvgString();
        if (svg) {
          blob = await captureSvgAsPng(svg, 3);
        }
      }

      if (!blob && iframeRef.current) {
        blob = await captureIframeAsPng(iframeRef.current);
      }

      if (blob) {
        downloadBlob(blob, `${baseName}.png`);
      }
    } catch (e) {
      console.error("PNG export failed:", e);
    } finally {
      setExporting(null);
      setIsOpen(false);
    }
  };

  const handleExportCode = () => {
    const ext = FILE_EXTENSIONS[language] || "txt";
    downloadText(code, `${baseName}.${ext}`);
    setIsOpen(false);
  };

  return (
    <div className={styles.container} ref={menuRef}>
      <button
        className={styles.trigger}
        onClick={() => setIsOpen((v) => !v)}
        disabled={!!exporting}
        title="Download"
      >
        {exporting ? (
          <span className={styles.spinner} />
        ) : (
          /* ✅ OLD DOWNLOAD ICON */
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
        )}
      </button>

      {isOpen && (
        <div className={styles.menu}>
          {/* Save as image – only for visual languages */}
          {showImageOption && (
            <>
              <button className={styles.menuItem} onClick={handleExportPng}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="9" cy="9" r="2" />
                  <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
                </svg>
                <span className={styles.menuItemText}>
                  <span className={styles.menuItemLabel}>Save as image</span>
                  <span className={styles.menuItemHint}>
                    {isSvgBased ? "High‑res PNG (3×)" : "PNG screenshot"}
                  </span>
                </span>
              </button>

              <div className={styles.menuDivider} />
            </>
          )}

          {/* Save as code / CSV */}
          <button className={styles.menuItem} onClick={handleExportCode}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="16 18 22 12 16 6" />
              <polyline points="8 6 2 12 8 18" />
            </svg>
            <span className={styles.menuItemText}>
              <span className={styles.menuItemLabel}>
                {isCsv ? "Save as CSV" : "Save as code"}
              </span>
              <span className={styles.menuItemHint}>
                {isCsv
                  ? "CSV file (.csv)"
                  : `${language} source (.${FILE_EXTENSIONS[language] || "txt"})`}
              </span>
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

export default ExportMenu;
